import React from "react";

import { css } from "@emotion/css";
import { CellContext, type SortingFn } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import { PatientListItem } from "@/models/PatientSummaryModel.ts";
import { doctorFormattedName } from "@/models/UserModel.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Typography } from "@/uiKit/atoms/Typography.tsx";

type TelemonitoringNurseColumnProps = CellContext<PatientListItem, unknown>;

export const TelemonitoringNurseColumn: React.FC<TelemonitoringNurseColumnProps> = ({ row }) => {
  const styles = useStyles(makeStyles);
  const nurse = row.original.telemonitoringNurse;

  return (
    <div className={styles.container}>
      <Typography className={styles.name} variant="body">
        {nurse ? doctorFormattedName(nurse.familyName, nurse.givenName) : ""}
      </Typography>
    </div>
  );
};

export const TelemonitoringNurseHeader: React.FC = () => {
  const { t } = useTranslation();
  const styles = useStyles(makeHeaderStyles);

  return (
    <div className={styles.container}>
      <Typography>{t("pages.patients.tlsNurse")}</Typography>
    </div>
  );
};

export const sortByNurseName: SortingFn<PatientListItem> = (rowA, rowB) =>
  doctorFormattedName(
    rowA.original.telemonitoringNurse?.familyName,
    rowA.original.telemonitoringNurse?.givenName,
  ).localeCompare(
    doctorFormattedName(rowB.original.telemonitoringNurse?.familyName, rowB.original.telemonitoringNurse?.givenName),
  );

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    flex-wrap: wrap;
    gap: ${theme.spacing(8)};
  `,
  name: css`
    font-weight: bold;
    text-wrap: wrap;
  `,
});

const makeHeaderStyles = () => ({
  container: css`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
  `,
});
